import { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useProducts } from "@/hooks/useProducts";
import { useCart } from "@/hooks/useCart";
import { useAuth } from "@/hooks/useAuth";
import { useUserPrice } from "@/hooks/useUserPrice";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import CopyTooltip from "./components/CopyTooltip";

export default function ProductDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { user } = useAuth();
  const { getPrice, getProfit, getProfitRate } = useUserPrice();
  const { products, isLoading: listLoading } = useProducts();

  const product = useMemo(() => products.find((p) => p.id === id), [products, id]);
  const [quantityStr, setQuantityStr] = useState("1");
  const [showCopyTip, setShowCopyTip] = useState(false);
  const [addedToCart, setAddedToCart] = useState(false);
  const [toastVisible, setToastVisible] = useState(false);
  const [stockWarning, setStockWarning] = useState("");

  const price = product ? getPrice(product) : 0;
  const profit = product ? getProfit(product) : 0;
  const profitRate = product ? getProfitRate(product) : 0;
  const isOutOfStock = product ? product.stock <= 0 : true;
  const maxQty = isOutOfStock ? 0 : Math.min(999, product.stock);

  // 普通分銷單筆最多認購 10 組
  const userLevel = user?.level;
  const isNormalDistributor = userLevel === "普通分銷";
  const effectiveMaxQty = isNormalDistributor ? Math.min(10, maxQty) : maxQty;

  // VIP 區域權限限制：普通分銷僅能購買「體驗認購區」
  const isVipRestricted = isNormalDistributor && product ? product.region !== "體驗認購區" : false;
  const canPurchase = !isOutOfStock && !isVipRestricted;

  // 解析輸入字串為有效數量，用於計算（小計與總利潤從字串即時推導，不綁定到 form 物件）
  const quantityNum = useMemo(() => {
    const n = parseInt(quantityStr, 10);
    if (Number.isNaN(n) || n <= 0) return 1;
    if (effectiveMaxQty > 0 && n > effectiveMaxQty) return effectiveMaxQty;
    return n;
  }, [quantityStr, effectiveMaxQty]);

  const totalCost = Math.round(price * quantityNum);
  const totalRefund = Math.round(Number(product?.suggestedPrice || 0) * quantityNum);

  // Loading state
  if (listLoading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-24 px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 animate-pulse">
            <div className="aspect-square bg-gray-200 rounded-xl" />
            <div className="space-y-4">
              <div className="h-8 bg-gray-200 rounded w-3/4" />
              <div className="h-4 bg-gray-200 rounded w-full" />
              <div className="h-4 bg-gray-200 rounded w-5/6" />
              <div className="grid grid-cols-2 gap-3 mt-6">
                <div className="h-24 bg-gray-200 rounded-xl" />
                <div className="h-24 bg-gray-200 rounded-xl" />
              </div>
              <div className="h-14 bg-gray-200 rounded-xl mt-4" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-24 text-center py-20">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <i className="ri-error-warning-line text-4xl text-gray-300"></i>
          </div>
          <p className="text-gray-500 text-lg mb-4">商品不存在或已下架</p>
          <button
            onClick={() => navigate("/products")}
            className="px-6 py-2.5 bg-amber-500 text-white rounded-full text-sm font-medium hover:bg-amber-600 transition-colors cursor-pointer"
          >
            返回商品列表
          </button>
        </div>
      </div>
    );
  }

  const handleAddToCart = () => {
    if (isVipRestricted) {
      setStockWarning("此區域僅限 VIP 會員認購，請聯繫管理員升級");
      return;
    }
    if (isOutOfStock) {
      setStockWarning("此商品已售完，無法加入購物車");
      return;
    }
    if (quantityNum <= 0) {
      setStockWarning("請輸入正確的購買數量");
      return;
    }
    if (quantityNum > product.stock) {
      setStockWarning(`庫存僅剩 ${product.stock} 件，無法購買 ${quantityNum} 件`);
      return;
    }
    setStockWarning("");
    const result = addToCart(product, quantityNum);
    if (!result.success) {
      setStockWarning(result.message || "普通分銷全站限購 10 組商品");
      return;
    }
    setAddedToCart(true);
    setToastVisible(true);
    setTimeout(() => {
      setAddedToCart(false);
      setToastVisible(false);
    }, 2200);
  };

  const handleCopyInfo = () => {
    if (!product) return;
    const text = `【${product.name}】\n${product.description}\n認購價：$${price} | 銷售價：$${product.suggestedPrice}`;
    navigator.clipboard.writeText(text).then(() => {
      setShowCopyTip(true);
      setTimeout(() => setShowCopyTip(false), 2000);
    });
  };

  const handleDownloadImage = () => {
    const link = document.createElement("a");
    link.href = product.images[0];
    link.download = `${product.name.replace(/\s+/g, "_")}.jpg`;
    link.target = "_blank";
    link.click();
  };

  const relatedProducts = products
    .filter((p) => p.id !== product.id && (p.category === product.category || p.tags.some((t) => product.tags.includes(t))))
    .slice(0, 4);

  return (
    <div className="min-h-screen bg-white">
      {/* Toast notification */}
      {toastVisible && (
        <div className="fixed top-20 right-6 z-50 bg-emerald-50 border border-emerald-200 rounded-xl px-5 py-3 flex items-center gap-3 shadow-lg animate-fade-in">
          <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
            <i className="ri-check-line text-emerald-600 text-sm"></i>
          </div>
          <div>
            <p className="text-sm font-medium text-emerald-800">已成功加入購物車</p>
            <p className="text-xs text-emerald-600">{product.name} x {quantityNum}</p>
          </div>
        </div>
      )}

      <Navbar />

      <div className="pt-20 pb-12">
        <div className="px-6 lg:px-12">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-gray-400 mb-6">
            <button onClick={() => navigate("/")} className="hover:text-amber-600 transition-colors cursor-pointer">首頁</button>
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-s-line text-xs"></i></div>
            <button onClick={() => navigate("/products")} className="hover:text-amber-600 transition-colors cursor-pointer">全部貨源</button>
            <div className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-s-line text-xs"></i></div>
            <span className="text-gray-700">{product.name}</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Image */}
            <div className="space-y-4">
              <div className="aspect-square rounded-xl overflow-hidden bg-gray-50 border border-gray-100">
                <img
                  src={product.images[0]}
                  alt={product.name}
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleDownloadImage}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 transition-colors cursor-pointer"
                >
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-download-line text-xs"></i></div>
                  下載主圖
                </button>
                <button
                  onClick={handleCopyInfo}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 transition-colors cursor-pointer"
                >
                  <div className="w-4 h-4 flex items-center justify-center"><i className="ri-file-copy-line text-xs"></i></div>
                  複製文案
                </button>
              </div>
              <CopyTooltip show={showCopyTip} />
            </div>

            {/* Info */}
            <div className="space-y-5">
              <div>
                <div className="flex flex-wrap gap-2 mb-3">
                  {product.isHot && (
                    <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-medium">爆款</span>
                  )}
                  {product.isNew && (
                    <span className="bg-emerald-500 text-white text-xs px-2 py-0.5 rounded-full font-medium">新品</span>
                  )}
                  {product.isOnSale && (
                    <span className="bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full font-medium">促銷</span>
                  )}
                  {isOutOfStock && (
                    <span className="bg-gray-400 text-white text-xs px-2 py-0.5 rounded-full font-medium">已售完</span>
                  )}
                </div>
                <h1 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">{product.name}</h1>
                <p className="text-gray-500 text-sm leading-relaxed">{product.description}</p>
              </div>

              {/* Price cards */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <p className="text-xs text-amber-700 mb-1">認購價（{user && user.role !== "admin" ? user.level : "我的成本"}）</p>
                  <p className="text-2xl font-bold text-amber-700">${(price || 0).toLocaleString('zh-TW')}</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                  <p className="text-xs text-gray-500 mb-1">銷售價</p>
                  <p className="text-2xl font-bold text-gray-700">${(Number(product.suggestedPrice) || 0).toLocaleString('zh-TW')}</p>
                </div>
              </div>

              {/* Profit highlight */}
              <div className={`flex items-center gap-3 border rounded-xl px-4 py-3 ${isOutOfStock ? 'bg-gray-50 border-gray-200' : 'bg-emerald-50 border-emerald-200'}`}>
                <div className={`w-10 h-10 flex items-center justify-center rounded-full ${isOutOfStock ? 'bg-gray-100' : 'bg-emerald-100'}`}>
                  <i className={`${isOutOfStock ? 'ri-close-circle-line text-gray-500' : 'ri-coins-line text-emerald-600'} text-lg`}></i>
                </div>
                <div>
                  <p className={`text-sm font-medium ${isOutOfStock ? 'text-gray-600' : 'text-emerald-800'}`}>
                    {isOutOfStock ? '暫無庫存' : `預期利潤：$${(profit || 0).toLocaleString('zh-TW')} / 件`}
                  </p>
                  <p className={`text-xs ${isOutOfStock ? 'text-gray-400' : 'text-emerald-600'}`}>
                    {isOutOfStock
                      ? '目前無法購買此商品'
                      : `利潤率 ${profitRate}% · 庫存 ${(product.stock || 0).toLocaleString('zh-TW')} 件`
                    }
                  </p>
                </div>
              </div>

              {/* Tags */}
              <div className="flex flex-wrap gap-2">
                {product.tags.map((tag) => (
                  <span key={tag} className="text-xs text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full">
                    {tag}
                  </span>
                ))}
              </div>

              {/* Quantity */}
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-600">數量</span>
                <div className="flex items-center border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setQuantityStr((prev) => {
                      const currentNum = parseInt(prev || "1", 10);
                      return String(Math.max(1, currentNum - 1));
                    })}
                    disabled={isOutOfStock}
                    className="w-10 h-10 flex items-center justify-center text-gray-500 hover:bg-gray-50 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <i className="ri-subtract-line"></i>
                  </button>
                  <input
                    type="number"
                    min={1}
                    max={effectiveMaxQty}
                    value={quantityStr}
                    onChange={(e) => {
                      const raw = e.target.value;
                      if (raw === "") {
                        setQuantityStr("");
                        setStockWarning("");
                        return;
                      }
                      const val = parseInt(raw, 10);
                      if (Number.isNaN(val)) return;
                      if (isNormalDistributor && val > 10) {
                        setQuantityStr("10");
                        setStockWarning("普通分銷最高認購 10 組");
                        return;
                      }
                      if (val > effectiveMaxQty && effectiveMaxQty > 0) {
                        setQuantityStr(effectiveMaxQty.toString());
                        setStockWarning(`庫存僅剩 ${product.stock} 件`);
                      } else {
                        setQuantityStr(val.toString());
                        setStockWarning("");
                      }
                    }}
                    onFocus={(e) => e.target.select()}
                    onBlur={() => {
                      const n = parseInt(quantityStr, 10);
                      if (Number.isNaN(n) || n <= 0) {
                        setQuantityStr("1");
                        setStockWarning("");
                      } else if (effectiveMaxQty > 0 && n > effectiveMaxQty) {
                        setQuantityStr(effectiveMaxQty.toString());
                        if (isNormalDistributor && product.stock > 10) {
                          setStockWarning("普通分銷最高認購 10 組");
                        } else {
                          setStockWarning(`庫存僅剩 ${product.stock} 件`);
                        }
                      } else {
                        setQuantityStr(n.toString());
                        setStockWarning("");
                      }
                    }}
                    disabled={isOutOfStock}
                    className="w-14 text-center text-sm font-medium outline-none border-none bg-transparent disabled:opacity-40"
                  />
                  <button
                    onClick={() => {
                      const currentNum = parseInt(quantityStr || "1", 10);
                      if (isNormalDistributor && currentNum >= 10) {
                        setQuantityStr("10");
                        setStockWarning("普通分銷最高認購 10 組");
                        return;
                      }
                      setQuantityStr(String(Math.min(effectiveMaxQty, currentNum + 1)));
                      setStockWarning("");
                    }}
                    disabled={isOutOfStock}
                    className="w-10 h-10 flex items-center justify-center text-gray-500 hover:bg-gray-50 cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <i className="ri-add-line"></i>
                  </button>
                </div>
              </div>

              {/* Total */}
              <div className="bg-stone-50 rounded-xl p-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-gray-600">小計</span>
                  <span className="text-lg font-bold text-gray-900">${(totalCost || 0).toLocaleString('zh-TW')}</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-emerald-600">銷售後返款</span>
                  <span className="font-medium text-emerald-600">+${(totalRefund || 0).toLocaleString('zh-TW')}</span>
                </div>
              </div>

              {/* Stock warning */}
              {stockWarning && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-600">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                      <i className="ri-error-warning-line text-red-500"></i>
                    </div>
                    <span>{stockWarning}</span>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3">
                <button
                  onClick={handleAddToCart}
                  disabled={!canPurchase}
                  className={`flex-1 py-3 rounded-xl text-white font-medium text-sm transition-all active:scale-[0.98] cursor-pointer whitespace-nowrap ${
                    !canPurchase
                      ? 'bg-gray-300 cursor-not-allowed'
                      : 'bg-amber-500 hover:bg-amber-600'
                  }`}
                >
                  {!canPurchase ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-lock-line"></i></div>
                      {isVipRestricted ? "僅限VIP會員認購" : "已售完"}
                    </span>
                  ) : addedToCart ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-check-line"></i></div>
                      已加入購物車
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 flex items-center justify-center"><i className="ri-shopping-cart-line"></i></div>
                      加入購物車
                    </span>
                  )}
                </button>
                {!user && (
                  <button
                    onClick={() => navigate("/login")}
                    className="px-6 py-3 rounded-xl border border-amber-500 text-amber-600 font-medium text-sm hover:bg-amber-50 transition-all cursor-pointer whitespace-nowrap"
                  >
                    登入分銷
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Related Products */}
          {relatedProducts.length > 0 && (
            <div className="mt-12">
              <h2 className="text-lg font-bold text-gray-900 mb-4">同類推薦</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {relatedProducts.map((p) => {
                  const pPrice = getPrice(p);
                  const pProfit = p.suggestedPrice - pPrice;
                  const pRate = Math.round((pProfit / p.suggestedPrice) * 100);
                  return (
                    <button
                      key={p.id}
                      onClick={() => navigate(`/products/${p.id}`)}
                      className="text-left bg-white rounded-xl overflow-hidden border border-gray-100 hover:border-amber-200 hover:shadow-md transition-all duration-300 cursor-pointer"
                    >
                      <div className="aspect-square overflow-hidden bg-gray-50">
                        <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover object-top" />
                      </div>
                      <div className="p-3">
                        <p className="text-sm text-gray-800 font-medium line-clamp-1">{p.name}</p>
                        <div className="flex items-end justify-between mt-1">
                          <span className="text-amber-600 font-bold text-sm">${(pPrice || 0).toLocaleString('zh-TW')}</span>
                          <span className="text-xs text-emerald-600">利潤 {pRate}%</span>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}